import { useState, useEffect } from "react";
import { Link } from "wouter";
import { X, Cookie, Shield, BarChart3 } from "lucide-react";

const CONSENT_KEY = "cqr_cookie_consent";
const CONSENT_EXPIRY_DAYS = 365;

function getStoredConsent(): string | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.expiry && Date.now() > parsed.expiry) {
      localStorage.removeItem(CONSENT_KEY);
      return null;
    }
    return parsed.value;
  } catch {
    return null;
  }
}

function storeConsent(value: "accepted" | "essential") {
  const expiry = Date.now() + CONSENT_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
  localStorage.setItem(CONSENT_KEY, JSON.stringify({ value, expiry }));
}

export function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = getStoredConsent();
    if (!consent) setVisible(true);
  }, []);

  const handleAccept = () => {
    storeConsent("accepted");
    setVisible(false);
  };

  const handleEssential = () => {
    storeConsent("essential");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6">
      <div className="max-w-4xl mx-auto bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl p-5 md:p-6">
        <div className="flex items-start gap-4">
          {/* Icon */}
          <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center">
            <Cookie className="w-5 h-5 text-indigo-400" />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-semibold text-sm md:text-base mb-1">
              We use cookies to improve your experience
            </h3>
            <p className="text-gray-400 text-xs md:text-sm leading-relaxed mb-4">
              CommonQR uses essential cookies to keep the platform running and analytics cookies to understand how you use it — helping us improve for everyone.{" "}
              <Link href="/privacy" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">
                Privacy Policy
              </Link>
            </p>

            {/* Cookie types */}
            <div className="flex flex-wrap gap-3 mb-4">
              <div className="flex items-center gap-1.5 text-xs text-gray-400">
                <Shield className="w-3.5 h-3.5 text-green-400" />
                <span>Essential — always on</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-gray-400">
                <BarChart3 className="w-3.5 h-3.5 text-indigo-400" />
                <span>Analytics — optional</span>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleAccept}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs md:text-sm font-medium rounded-lg transition-colors"
              >
                Accept All
              </button>
              <button
                onClick={handleEssential}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 text-xs md:text-sm font-medium rounded-lg transition-colors"
              >
                Essential Only
              </button>
            </div>
          </div>

          {/* Close */}
          <button
            onClick={handleEssential}
            className="flex-shrink-0 text-gray-500 hover:text-gray-300 transition-colors mt-0.5"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// Hook for other components to read consent
export function useCookieConsent(): "accepted" | "essential" | null {
  return getStoredConsent() as "accepted" | "essential" | null;
}
